import { useEffect, useState } from "react";
import axios from "axios";
import { useQuery } from "react-query";

import { INIT_FILTER_VALUES } from "../../wadtConstants";
import { useAuth0 } from "@auth0/auth0-react";

/**
 * Custom hook responsible for controlling the state related to the
 * Map filter controls
 * Accepts one argument, `onFilterChange` that is a callback that
 * can be run whenever a filter value changes
 */
const useWadtFilters = ({ onFilterChange }) => {
  const [filterValues, setFilterValues] = useState(INIT_FILTER_VALUES);
  const { isAuthenticated, getAccessTokenSilently } = useAuth0();

  /**
   * Hit the API and get a list of options for each filter
   * The endpoint returns an object where each key/value pair
   * represents the options for a specific filter
   * This approach allows us to make a single API request instead
   * of multiple
   */
  const { data } = useQuery(
    ["wadtFilterData", isAuthenticated],
    async () => {
      let headers = {};
      if (isAuthenticated) {
        const token = await getAccessTokenSilently();
        headers = { Authorization: `Bearer ${token}` };
      }
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_ENDPOINT}/api/wadt-map/filters`,
          { headers }
        );
        return response;
      } catch (err) {
        console.error(err);
      }
    },
    {
      refetchOnWindowFocus: false,
    }
  );

  /**
   * Set the options and initial value for each filter based on
   * what is returned from the DB
   */
  useEffect(() => {
    setFilterValues((prevState) => ({
      ...prevState,
      classes: {
        ...prevState.classes,
        options: data?.data?.classes || [],
        value: data?.data?.classes.map(({ value }) => value) || [],
      },

      structures: {
        ...prevState.structures,
        options: data?.data?.structures || [],
        value: data?.data?.structures.map(({ value }) => value) || [],
      },
      waterSources: {
        ...prevState.waterSources,
        options: data?.data?.waterSources || [],
        value: data?.data?.waterSources.map(({ value }) => value) || [],
      },
    }));
  }, [data?.data]);

  const handleSelectAll = (name) => {
    setFilterValues((prevState) => {
      const newState = {
        ...prevState,
        [name]: {
          ...prevState[name],
          value: data?.data?.[name]?.map(({ value }) => value),
        },
      };
      onFilterChange(newState);
      return newState;
    });
  };

  const handleSelectNone = (name) => {
    setFilterValues((prevState) => {
      const newState = {
        ...prevState,
        [name]: {
          ...prevState[name],
          value: [],
        },
      };
      onFilterChange(newState);
      return newState;
    });
  };

  const handleFilterValues = (event) => {
    const { checked, name, value } = event.target;

    const type = filterValues[name].type;

    if (type === "multi-select" || type === "multi-select-array") {
      setFilterValues((prevState) => {
        const newValue = [...prevState[name].value];
        const existingIndex = newValue.indexOf(value);
        if (existingIndex > -1) {
          newValue.splice(existingIndex, 1);
        } else {
          newValue.push(value);
        }
        const newState = {
          ...prevState,
          [name]: {
            ...prevState[name],
            value: newValue,
          },
        };
        onFilterChange(newState);
        return newState;
      });
    } else if (type === "boolean") {
      setFilterValues((prevState) => {
        const newState = {
          ...prevState,
          [name]: {
            ...prevState[name],
            value: checked,
          },
        };
        onFilterChange(newState);
        return newState;
      });
    } else if (type === "select") {
      setFilterValues((prevState) => {
        const newState = {
          ...prevState,
          [name]: {
            ...prevState[name],
            value: value,
          },
        };
        // onFilterChange(newState);
        return newState;
      });
    }
  };

  return {
    filterValues,
    setFilterValues,
    handleFilterValues,
    handleSelectAll,
    handleSelectNone,
  };
};

export default useWadtFilters;
